/**
 * Some catalog `product_type` (and related taxonomy) values arrive as full
 * Google-Product-Category breadcrumb paths — e.g. `Clothing > Activewear > …
 * > Biker Jumpsuit` — instead of a clean leaf name. Display only the leaf (the
 * last `>`-delimited segment); values without a separator pass through
 * untouched. Display-only: callers must keep the raw value for selection state
 * and the filter API payload, since the backend matches on the raw value.
 */
export function toFilterOptionLabel(value: string): string {
  if (!value.includes('>')) return value;
  const leaf = value.split('>').pop()?.trim() ?? '';
  return leaf.length > 0 ? leaf : value;
}

/**
 * Filter keys whose option values are taxonomy breadcrumbs that must be shown
 * in full rather than collapsed to their leaf. For product type the position in
 * the hierarchy is the meaningful part — two catalogs can both have "Jackets"
 * under entirely different parents, and picking a scope on the leaf alone is
 * ambiguous.
 */
const FULL_PATH_FILTER_KEYS = new Set(['product_type']);

/** Whether a filter's options should display their full breadcrumb path. */
export function isFullPathFilterKey(key: string): boolean {
  return FULL_PATH_FILTER_KEYS.has(key);
}

/** Immediate parent segment of a breadcrumb path (the part before the leaf), or null. */
function parentSegment(value: string): string | null {
  const parts = value
    .split('>')
    .map((part) => part.trim())
    .filter((part) => part.length > 0);
  return parts.length >= 2 ? (parts[parts.length - 2] ?? null) : null;
}

/**
 * Builds display labels for a set of filter options, keyed by their raw value.
 * Normally each label is the clean leaf name; when two distinct raw values
 * collapse to the same leaf (e.g. `Activewear > Joggers` and `Loungewear >
 * Joggers`), the colliding ones are disambiguated with their parent segment —
 * `Joggers (Activewear)` — and fall back to the full raw value if the parent is
 * still not enough. Keeps a 1:1 row↔value mapping so filtering is unaffected.
 */
export function buildFilterOptionLabels(values: string[]): Map<string, string> {
  const leafByValue = new Map<string, string>();
  const leafCount = new Map<string, number>();
  for (const value of values) {
    const leaf = toFilterOptionLabel(value);
    leafByValue.set(value, leaf);
    leafCount.set(leaf, (leafCount.get(leaf) ?? 0) + 1);
  }

  // Disambiguate only the leaves that more than one raw value collapses to.
  const candidate = new Map<string, string>();
  for (const value of values) {
    const leaf = leafByValue.get(value) ?? value;
    if ((leafCount.get(leaf) ?? 0) <= 1) {
      candidate.set(value, leaf);
      continue;
    }
    const parent = parentSegment(value);
    candidate.set(value, parent ? `${leaf} (${parent})` : leaf);
  }

  // If parent context still isn't enough to tell two rows apart, fall back to
  // the raw value (always unique) so no two rows ever read identically.
  const candidateCount = new Map<string, number>();
  for (const label of candidate.values()) {
    candidateCount.set(label, (candidateCount.get(label) ?? 0) + 1);
  }
  const labels = new Map<string, string>();
  for (const value of values) {
    const label = candidate.get(value) ?? value;
    labels.set(value, (candidateCount.get(label) ?? 0) > 1 ? value : label);
  }
  return labels;
}

/**
 * Display labels for one filter's options, honouring {@link isFullPathFilterKey}:
 * breadcrumb filters (product type) map each value to itself so the full path
 * renders, everything else gets the collision-aware leaf labels.
 */
export function buildFilterOptionLabelsForKey(key: string, values: string[]): Map<string, string> {
  if (isFullPathFilterKey(key)) return new Map(values.map((value) => [value, value]));
  return buildFilterOptionLabels(values);
}
